import * as React from 'react';
import { BreakpointSizeType } from '../../../theme/type/breakpoints-type';
import { GapSizeType } from '../../../theme/type/gap-type';
import { cn } from '../../../utils/tailwind';

type ResponsiveGapType = Partial<Record<BreakpointSizeType, GapSizeType>>;

const dividerGapClass: Record<GapSizeType, string> = {
    none: 'my-0',
    xxsmall: 'my-xxsmall',
    xsmall: 'my-xsmall',
    vsmall: 'my-vsmall',
    small: 'my-small',
    medium: 'my-medium',
    large: 'my-large',
    vlarge: 'my-vlarge',
    xlarge: 'my-xlarge',
    xxlarge: 'my-xxlarge',
};

const responsiveDividerGapClass: Record<BreakpointSizeType, Record<GapSizeType, string>> = {
    xs: {
        none: 'xs:my-0',
        xxsmall: 'xs:my-xxsmall',
        xsmall: 'xs:my-xsmall',
        vsmall: 'xs:my-vsmall',
        small: 'xs:my-small',
        medium: 'xs:my-medium',
        large: 'xs:my-large',
        vlarge: 'xs:my-vlarge',
        xlarge: 'xs:my-xlarge',
        xxlarge: 'xs:my-xxlarge',
    },
    vs: {
        none: 'vs:my-0',
        xxsmall: 'vs:my-xxsmall',
        xsmall: 'vs:my-xsmall',
        vsmall: 'vs:my-vsmall',
        small: 'vs:my-small',
        medium: 'vs:my-medium',
        large: 'vs:my-large',
        vlarge: 'vs:my-vlarge',
        xlarge: 'vs:my-xlarge',
        xxlarge: 'vs:my-xxlarge',
    },
    sm: {
        none: 'sm:my-0',
        xxsmall: 'sm:my-xxsmall',
        xsmall: 'sm:my-xsmall',
        vsmall: 'sm:my-vsmall',
        small: 'sm:my-small',
        medium: 'sm:my-medium',
        large: 'sm:my-large',
        vlarge: 'sm:my-vlarge',
        xlarge: 'sm:my-xlarge',
        xxlarge: 'sm:my-xxlarge',
    },
    md: {
        none: 'md:my-0',
        xxsmall: 'md:my-xxsmall',
        xsmall: 'md:my-xsmall',
        vsmall: 'md:my-vsmall',
        small: 'md:my-small',
        medium: 'md:my-medium',
        large: 'md:my-large',
        vlarge: 'md:my-vlarge',
        xlarge: 'md:my-xlarge',
        xxlarge: 'md:my-xxlarge',
    },
    lg: {
        none: 'lg:my-0',
        xxsmall: 'lg:my-xxsmall',
        xsmall: 'lg:my-xsmall',
        vsmall: 'lg:my-vsmall',
        small: 'lg:my-small',
        medium: 'lg:my-medium',
        large: 'lg:my-large',
        vlarge: 'lg:my-vlarge',
        xlarge: 'lg:my-xlarge',
        xxlarge: 'lg:my-xxlarge',
    },
    vl: {
        none: 'vl:my-0',
        xxsmall: 'vl:my-xxsmall',
        xsmall: 'vl:my-xsmall',
        vsmall: 'vl:my-vsmall',
        small: 'vl:my-small',
        medium: 'vl:my-medium',
        large: 'vl:my-large',
        vlarge: 'vl:my-vlarge',
        xlarge: 'vl:my-xlarge',
        xxlarge: 'vl:my-xxlarge',
    },
    xl: {
        none: 'xl:my-0',
        xxsmall: 'xl:my-xxsmall',
        xsmall: 'xl:my-xsmall',
        vsmall: 'xl:my-vsmall',
        small: 'xl:my-small',
        medium: 'xl:my-medium',
        large: 'xl:my-large',
        vlarge: 'xl:my-vlarge',
        xlarge: 'xl:my-xlarge',
        xxlarge: 'xl:my-xxlarge',
    },
    xxl: {
        none: 'xxl:my-0',
        xxsmall: 'xxl:my-xxsmall',
        xsmall: 'xxl:my-xsmall',
        vsmall: 'xxl:my-vsmall',
        small: 'xxl:my-small',
        medium: 'xxl:my-medium',
        large: 'xxl:my-large',
        vlarge: 'xxl:my-vlarge',
        xlarge: 'xxl:my-xlarge',
        xxlarge: 'xxl:my-xxlarge',
    },
    fullhd: {
        none: 'fullhd:my-0',
        xxsmall: 'fullhd:my-xxsmall',
        xsmall: 'fullhd:my-xsmall',
        vsmall: 'fullhd:my-vsmall',
        small: 'fullhd:my-small',
        medium: 'fullhd:my-medium',
        large: 'fullhd:my-large',
        vlarge: 'fullhd:my-vlarge',
        xlarge: 'fullhd:my-xlarge',
        xxlarge: 'fullhd:my-xxlarge',
    },
};

const getDividerGapClassName = (gap: GapSizeType | object) => {
    if (typeof gap === 'string') {
        return dividerGapClass[gap];
    }

    const gapObject = gap as ResponsiveGapType;
    const entries = Object.entries(gapObject) as [BreakpointSizeType, GapSizeType][];

    if (entries.length === 0) {
        return dividerGapClass.vlarge;
    }

    return entries
        .map(([breakpoint, size], index) => {
            if (index === 0) {
                return dividerGapClass[size];
            }

            return responsiveDividerGapClass[breakpoint][size];
        })
        .join(' ');
};

interface DividerPropsInterface {
    children?: React.ReactNode;
    gap?: GapSizeType | object;
    variant?: 'solid' | 'dashed';
    borderColor?: 'divider' | 'button';
}

export const Divider: React.FC<DividerPropsInterface> = ({
    gap = 'vlarge',
    variant,
    borderColor = 'divider',
    children,
}) => {
    const borderColorClassName =
        borderColor === 'button' ? 'border-divider/30' : 'border-divider/12 dark:border-divider/10';
    const className = cn(
        'relative h-0 w-full border-0 border-b-[0.1rem] text-[0]',
        borderColorClassName,
        variant === 'dashed' ? 'border-dashed' : 'border-solid',
        getDividerGapClassName(gap),
    );
    const lineClassName = cn(
        'h-0 flex-1 border-0 border-b-[0.1rem]',
        borderColorClassName,
        variant === 'dashed' ? 'border-dashed' : 'border-solid',
    );

    if (!children) {
        return <hr className={className} />;
    }

    return (
        <div className={cn('flex w-full items-center gap-xsmall', getDividerGapClassName(gap))}>
            <span className={lineClassName} />
            <span className="inline-flex text-small font-medium text-neutral-gray">{children}</span>
            <span className={lineClassName} />
        </div>
    );
};
